import { IResponse } from 'types';

export const DB_CACHE_TTL_MS = 3 * 60 * 60 * 1000;
const DB_NAME = 'lodgefirst_hotel_cache';
const DB_VERSION = 1;
const STORE_NAME = 'tables';

export type DbCacheTable =
  | 'info'
  | 'rooms'
  | 'logs'
  | 'customers'
  | 'users'
  | 'hotel';

const STALE_CACHE_KEY_MARKERS = ['_branchfiles', '_branchfile_'];

type CacheRecord = {
  data: unknown;
  fetchedAt: number;
};

export type DbCacheContext = {
  hotelId: string;
  userEmail?: string;
};

const dbPromise: { current: Promise<IDBDatabase> | null } = { current: null };

const sanitizeSegment = (value: string) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

export const buildStoreName = (
  hotelId: string,
  table: DbCacheTable,
  userEmail?: string,
  cacheKey?: string,
): string => {
  const hotelSegment = sanitizeSegment(String(hotelId));
  if (table === 'users') {
    const emailSegment = sanitizeSegment(userEmail || 'anonymous');
    return `${hotelSegment}_users_${emailSegment}`;
  }
  if (cacheKey) {
    return `${hotelSegment}_${table}_${sanitizeSegment(cacheKey)}`;
  }
  return `${hotelSegment}_${table}`;
};

export const isDbCacheValid = (fetchedAt: number, now = Date.now()) => (
  now - fetchedAt < DB_CACHE_TTL_MS
);

const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise.current) return dbPromise.current;

  dbPromise.current = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbPromise.current = null;
      reject(request.error || new Error('Failed to open IndexedDB'));
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
  });

  return dbPromise.current;
};

const idbRequest = <T>(request: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
});

export const getDbCache = async <T>(
  storeName: string,
): Promise<{ data: T; fetchedAt: number } | null> => {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const record = await idbRequest<CacheRecord | undefined>(
      tx.objectStore(STORE_NAME).get(storeName),
    );
    if (!record || record.data === undefined) return null;
    return { data: record.data as T, fetchedAt: record.fetchedAt };
  } catch {
    return null;
  }
};

export const setDbCache = async (storeName: string, data: unknown): Promise<void> => {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const record: CacheRecord = { data, fetchedAt: Date.now() };
    await idbRequest(tx.objectStore(STORE_NAME).put(record, storeName));
  } catch {
    // Non-fatal: app continues with network-only data.
  }
};

/** Reset the 3-minute TTL without changing cached payload. */
export const renewDbCacheTtl = async (storeName: string): Promise<void> => {
  const existing = await getDbCache(storeName);
  if (!existing || existing.data === undefined) return;
  await setDbCache(storeName, existing.data);
};

export const renewAllHotelDbCacheTtls = async (ctx: DbCacheContext): Promise<void> => {
  if (!ctx.hotelId) return;
  const prefix = `${sanitizeSegment(String(ctx.hotelId))}_`;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const keys = await idbRequest<IDBValidKey[]>(store.getAllKeys());
    const now = Date.now();
    await Promise.all(
      keys
        .map((key) => String(key))
        .filter((key) => key.startsWith(prefix))
        .map(async (key) => {
          const record = await idbRequest<CacheRecord | undefined>(store.get(key));
          if (record?.data === undefined) return;
          await idbRequest(store.put({ data: record.data, fetchedAt: now }, key));
        }),
    );
  } catch {
    // ignore
  }
};

export const patchDbCache = async (
  storeName: string,
  updater: (current: unknown | null) => unknown,
): Promise<void> => {
  const existing = await getDbCache(storeName);
  const next = updater(existing?.data ?? null);
  await setDbCache(storeName, next);
};

export const deleteDbCache = async (storeName: string): Promise<void> => {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(tx.objectStore(STORE_NAME).delete(storeName));
  } catch {
    // ignore
  }
};

export const purgeOtherHotels = async (hotelId: string): Promise<void> => {
  const prefix = `${sanitizeSegment(String(hotelId))}_`;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const keys = await idbRequest<IDBValidKey[]>(store.getAllKeys());
    await Promise.all(
      keys
        .map((key) => String(key))
        .filter((key) => (
          !key.startsWith(prefix)
          || STALE_CACHE_KEY_MARKERS.some((marker) => key.includes(marker))
        ))
        .map((key) => idbRequest(store.delete(key))),
    );
  } catch {
    // ignore
  }
};

/** Delete every IndexedDB cache entry for the active hotel. */
export const purgeHotelDbCache = async (hotelId: string): Promise<void> => {
  if (!hotelId) return;
  const prefix = `${sanitizeSegment(String(hotelId))}_`;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const keys = await idbRequest<IDBValidKey[]>(store.getAllKeys());
    await Promise.all(
      keys
        .map((key) => String(key))
        .filter((key) => key.startsWith(prefix))
        .map((key) => idbRequest(store.delete(key))),
    );
  } catch {
    // ignore
  }
};

/** True when info payload includes a real users list (not still-loading null). */
export const isInfoCacheComplete = (data: unknown): boolean => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  return Array.isArray((data as Record<string, unknown>).users);
};

export const syncInfoDerivedCaches = async (
  ctx: DbCacheContext,
  infoData: Record<string, unknown>,
): Promise<void> => {
  if (!ctx.hotelId) return;
  // Never persist incomplete Redux settings (users still null) — that poisons
  // getInfo() cache hits and shows every audit actor as "Unresolved".
  if (!isInfoCacheComplete(infoData)) {
    if (infoData.customers !== undefined) {
      await setDbCache(buildStoreName(ctx.hotelId, 'customers'), { customers: infoData.customers });
    }
    return;
  }
  const infoStore = buildStoreName(ctx.hotelId, 'info');
  await setDbCache(infoStore, infoData);
  await setDbCache(
    buildStoreName(ctx.hotelId, 'users', ctx.userEmail),
    infoData.users,
  );
  if (infoData.customers !== undefined) {
    await setDbCache(buildStoreName(ctx.hotelId, 'customers'), { customers: infoData.customers });
  }
};

export type FetchDbDataParams<T = unknown> = {
  table: DbCacheTable;
  cacheKey?: string;
  forceRefresh?: boolean;
  fetchFromBe: () => Promise<IResponse>;
  onCachedHit?: (data: T) => void;
};

export const fetchDbDataWithCache = async <T = unknown>(
  ctx: DbCacheContext,
  params: FetchDbDataParams<T>,
): Promise<IResponse> => {
  if (!ctx.hotelId) {
    return params.fetchFromBe();
  }

  const storeName = buildStoreName(
    ctx.hotelId,
    params.table,
    ctx.userEmail,
    params.cacheKey,
  );

  if (!params.forceRefresh) {
    const cached = await getDbCache<T>(storeName);
    if (cached && isDbCacheValid(cached.fetchedAt)) {
      // Skip poisoned/incomplete info entries written before users finished loading.
      if (params.table === 'info' && !isInfoCacheComplete(cached.data)) {
        // fall through to network fetch
      } else {
        params.onCachedHit?.(cached.data);
        return { status: 'success', data: cached.data };
      }
    }
  }

  const res = await params.fetchFromBe();
  if (res.status === 'success' && res.data !== undefined) {
    if (params.table === 'info') {
      await syncInfoDerivedCaches(ctx, res.data);
    } else {
      await setDbCache(storeName, res.data);
    }
  }
  return res;
};

export const getDbCacheContextFromState = (state: {
  views?: { hotelData?: { id?: string | number } | null };
  app?: { userEmail?: string };
  global?: { username?: string | null };
  settings?: {
    parts?: Record<string, unknown>;
    customers?: unknown;
    crmSettings?: unknown;
  };
  logs?: {
    logs?: unknown;
  };
}): DbCacheContext => {
  const hotelId = state.views?.hotelData?.id;
  const userEmail = state.app?.userEmail || state.global?.username || undefined;
  return {
    hotelId: hotelId != null ? String(hotelId) : '',
    userEmail: userEmail || undefined,
  };
};

/**
 * Persist current Redux hotelData into the IndexedDB `hotel` cache.
 * gethotel cache shape is an array: [hotelRow].
 * Without this, syncAppDbCache only renews TTL and refresh serves stale hotel
 * (e.g. inventory missing after upload until force refresh).
 */
export const refreshHotelCacheFromState = async (
  state: Parameters<typeof getDbCacheContextFromState>[0],
): Promise<void> => {
  const ctx = getDbCacheContextFromState(state);
  const hotelData = state.views?.hotelData;
  if (!ctx.hotelId || !hotelData || typeof hotelData !== 'object') return;

  const storeName = buildStoreName(ctx.hotelId, 'hotel');
  const existing = await getDbCache<unknown>(storeName);
  const cached = existing?.data;

  if (Array.isArray(cached) && cached[0] && typeof cached[0] === 'object') {
    await setDbCache(storeName, [{ ...(cached[0] as object), ...hotelData }]);
    return;
  }
  if (cached && typeof cached === 'object' && !Array.isArray(cached)) {
    await setDbCache(storeName, { ...(cached as object), ...hotelData });
    return;
  }
  await setDbCache(storeName, [hotelData]);
};

/** Mirror current Redux settings slice into IndexedDB after local or remote updates. */
export const refreshInfoCacheFromState = async (
  state: Parameters<typeof getDbCacheContextFromState>[0],
): Promise<void> => {
  const ctx = getDbCacheContextFromState(state);
  if (!ctx.hotelId) return;
  const { parts, customers, crmSettings } = state.settings || {};
  await syncInfoDerivedCaches(ctx, {
    users: parts?.users,
    groups: parts?.groups,
    roomTypes: parts?.roomTypes,
    rates: parts?.rates,
    services: parts?.services,
    billing: parts?.billing,
    customers,
    crmSettings,
  });
};

export const refreshRoomsCacheFromState = async (
  state: Parameters<typeof getDbCacheContextFromState>[0] & { app?: { rooms?: unknown } },
): Promise<void> => {
  const ctx = getDbCacheContextFromState(state);
  if (!ctx.hotelId || state.app?.rooms == null) return;
  await setDbCache(buildStoreName(ctx.hotelId, 'rooms'), state.app.rooms);
};

export const refreshLogsCacheFromState = async (
  state: Parameters<typeof getDbCacheContextFromState>[0],
): Promise<void> => {
  const ctx = getDbCacheContextFromState(state);
  if (!ctx.hotelId || state.logs?.logs == null) return;
  await setDbCache(buildStoreName(ctx.hotelId, 'logs'), state.logs.logs);
};

export const refreshCustomersCacheFromState = async (
  state: Parameters<typeof getDbCacheContextFromState>[0],
): Promise<void> => {
  const ctx = getDbCacheContextFromState(state);
  if (!ctx.hotelId || state.settings?.customers === undefined) return;
  await setDbCache(
    buildStoreName(ctx.hotelId, 'customers'),
    { customers: state.settings.customers },
  );
};

/**
 * After a local or socket-driven Redux update: persist latest state and restart
 * the 3-minute TTL on every IndexedDB entry for the active hotel.
 */
export const syncAllDbCachesFromState = async (
  state: Parameters<typeof getDbCacheContextFromState>[0] & { app?: { rooms?: unknown } },
): Promise<void> => {
  const ctx = getDbCacheContextFromState(state);
  if (!ctx.hotelId) return;

  await Promise.all([
    refreshHotelCacheFromState(state),
    refreshInfoCacheFromState(state),
    refreshRoomsCacheFromState(state),
    refreshLogsCacheFromState(state),
    refreshCustomersCacheFromState(state),
  ]);
  await renewAllHotelDbCacheTtls(ctx);
};
